import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  TableRow,
  TableCell,
  IconButton,
  Chip,
  Tooltip,
  Collapse,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import StorageIcon from '@mui/icons-material/Storage';
import FavoriteButton from '../../../../components/olymp/FavoriteButton';
import ListButton from '../../../../components/olymp/ListButton';
import SensitivityBadge from '../../../../components/olymp/SensitivityBadge';
import FrequencyCell from '../../../../components/olymp/FrequencyCell';
import ExpandedRow from './ExpandedRow';
import { getPrimaryName as getDefaultPrimaryName, getOMOPStatusColor, getOMOPStatusLabel } from '../utils/helpers';
import { getParameter, getParameterLookupId } from '../../../../services/catalogService';
import { useTranslation } from 'react-i18next';

/**
 * Parameter Row Component
 * Renders a single parameter row in the data catalog table
 *
 * @param {Object} param - Parameter data
 * @param {string} nameView - Name view mode ('charite', 'omop', 'both')
 * @param {boolean} isExpanded - Whether row is expanded
 * @param {Function} onRowClick - Handler for row click
 * @param {Function} onFavoriteToggle - Handler for favorite toggle
 * @param {Array} activeWorkingList - Active working list IDs
 * @param {Array} parameterLists - All parameter lists
 * @param {Function} getParameterListStatus - Get list status for parameter
 * @param {Function} isParameterInAnyList - Check if parameter is in any list
 * @param {Function} handleRemoveFromList - Remove parameter from list
 * @param {Function} handleAddToList - Add parameter to list
 * @param {Function} handleOpenListDialog - Open list dialog
 * @param {Array} allTagsWithStats - All tags with statistics
 * @param {Function} getIconComponent - Get icon component by name
 * @param {Function} getPrimaryDescription - Get primary description
 * @param {Object} subtlePulse - Animation for deletion requests
 */
const ParameterRow = ({
  param,
  nameView,
  isExpanded,
  onRowClick,
  onFavoriteToggle,
  activeWorkingList,
  parameterLists,
  getParameterListStatus,
  isParameterInAnyList,
  handleRemoveFromList,
  handleAddToList,
  handleOpenListDialog,
  allTagsWithStats,
  getIconComponent,
  getPrimaryDescription,
  subtlePulse,
}) => {
  const { t } = useTranslation();
  const [detailParam, setDetailParam] = useState(param.hasDetailData ? param : null);
  const [detailStatus, setDetailStatus] = useState(param.hasDetailData ? 'success' : 'idle');
  const [detailError, setDetailError] = useState(null);

  const getPrimaryName = (parameter) => getDefaultPrimaryName(parameter, nameView);

  useEffect(() => {
    if (param.hasDetailData) {
      setDetailParam(param);
      setDetailStatus('success');
      setDetailError(null);
    }
  }, [param]);

  useEffect(() => {
    let cancelled = false;

    async function loadDetail() {
      setDetailStatus('loading');
      setDetailError(null);

      const result = await getParameter(param);
      if (cancelled) {
        return;
      }

      if (result.source === 'api' && result.parameter) {
        setDetailParam(result.parameter);
        setDetailStatus('success');
        return;
      }

      setDetailStatus('error');
      setDetailError(t('dataCatalog.status.detailLoadFailed'));
    }

    if (isExpanded && !param.hasDetailData && !(detailParam?.id === param.id && detailParam?.hasDetailData)) {
      loadDetail();
    }

    return () => {
      cancelled = true;
    };
  }, [detailParam, isExpanded, param.hasDetailData, param.id, t]);

  const resolvedParam = detailParam || param;
  const expandedLoading = isExpanded && !resolvedParam.hasDetailData && detailStatus !== 'error';

  return (
    <React.Fragment key={param.id}>
      <TableRow
        hover
        onClick={() => onRowClick(param.id)}
        sx={{
          cursor: 'pointer',
          '& > *': { borderBottom: 'unset' }
        }}
      >
        {activeWorkingList.length === 1 && parameterLists && (() => {
          const listA = parameterLists.find(l => l.id === activeWorkingList[0]);
          const inListA = listA && listA.parameterIds.includes(param.id);

          return (
            <TableCell align="center">
              {inListA ? (
                <Tooltip title={t('dataCatalog.actions.inListClickRemove', { list: listA?.name })}>
                  <Box
                    onClick={(e) => handleRemoveFromList(param.id, activeWorkingList[0], e)}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      backgroundColor: '#2196f3',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#1976d2',
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.2s'
                    }}
                  >
                    A
                  </Box>
                </Tooltip>
              ) : (
                <Tooltip title={t('dataCatalog.actions.addToListNamed', { list: listA?.name })}>
                  <Box
                    onClick={(e) => handleAddToList(param.id, activeWorkingList[0], e)}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      border: '2px solid #2196f3',
                      backgroundColor: 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#2196f3',
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(33, 150, 243, 0.1)',
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.2s'
                    }}
                  >
                    A
                  </Box>
                </Tooltip>
              )}
            </TableCell>
          );
        })()}

        {activeWorkingList.length === 2 && parameterLists && (() => {
          const status = getParameterListStatus(param.id);
          const listA = parameterLists.find(l => l.id === activeWorkingList[0]);
          const listB = parameterLists.find(l => l.id === activeWorkingList[1]);

          const inListA = status === 'list1Only' || status === 'both';
          const inListB = status === 'list2Only' || status === 'both';

          return (
            <>
              <TableCell align="center">
                {inListA ? (
                  <Tooltip title={t('dataCatalog.actions.inListAClickRemove', { list: listA?.name })}>
                    <Box
                      onClick={(e) => handleRemoveFromList(param.id, activeWorkingList[0], e)}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        backgroundColor: '#2196f3',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#1976d2',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s'
                      }}
                    >
                      A
                    </Box>
                  </Tooltip>
                ) : (
                  <Tooltip title={t('dataCatalog.actions.addToListA', { list: listA?.name })}>
                    <Box
                      onClick={(e) => handleAddToList(param.id, activeWorkingList[0], e)}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        border: '2px solid #2196f3',
                        backgroundColor: 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#2196f3',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(33, 150, 243, 0.1)',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s'
                      }}
                    >
                    </Box>
                  </Tooltip>
                )}
              </TableCell>
              <TableCell align="center">
                {inListB ? (
                  <Tooltip title={t('dataCatalog.actions.inListBClickRemove', { list: listB?.name })}>
                    <Box
                      onClick={(e) => handleRemoveFromList(param.id, activeWorkingList[1], e)}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        backgroundColor: '#4caf50',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#388e3c',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s'
                      }}
                    >
                      B
                    </Box>
                  </Tooltip>
                ) : (
                  <Tooltip title={t('dataCatalog.actions.addToListB', { list: listB?.name })}>
                    <Box
                      onClick={(e) => handleAddToList(param.id, activeWorkingList[1], e)}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        border: '2px solid #4caf50',
                        backgroundColor: 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#4caf50',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(76, 175, 80, 0.1)',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s'
                      }}
                    >
                    </Box>
                  </Tooltip>
                )}
              </TableCell>
            </>
          );
        })()}

        <TableCell>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRowClick(param.id);
            }}
          >
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        <TableCell>
          <Tooltip
            title={
              <Box>
                <Typography variant="caption" display="block">
                  <strong>{t('dataCatalog.table.tooltips.local')}:</strong> {resolvedParam.name}
                </Typography>
                {resolvedParam.omopName && (
                  <Typography variant="caption" display="block">
                    <strong>{t('dataCatalog.table.tooltips.omop')}:</strong> {resolvedParam.omopName}
                  </Typography>
                )}
                {resolvedParam.omopCode && (
                  <Typography variant="caption" display="block">
                    <strong>{t('dataCatalog.table.tooltips.code')}:</strong> {resolvedParam.omopCode}
                  </Typography>
                )}
                {resolvedParam.omopVocabulary && (
                  <Typography variant="caption" display="block">
                    <strong>{t('dataCatalog.table.tooltips.vocabulary')}:</strong> {resolvedParam.omopVocabulary}
                  </Typography>
                )}
              </Box>
            }
            placement="right"
            arrow
          >
            <Box>
              {nameView === 'both' ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight="bold" sx={{ minWidth: 60 }}>
                      🏥 {t('dataCatalog.table.tooltips.local')}:
                      </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {resolvedParam.name}
                    </Typography>
                  </Box>
                  {resolvedParam.omopName && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <Typography variant="caption" color="primary" fontWeight="bold" sx={{ minWidth: 60 }}>
                        🔷 {t('dataCatalog.table.tooltips.omop')}:
                      </Typography>
                      <Typography variant="body2" color="primary">
                        {resolvedParam.omopName}
                      </Typography>
                    </Box>
                  )}
                </>
              ) : (
                <Typography variant="body1" fontWeight="bold">
                  {getPrimaryName(resolvedParam)}
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" color="textSecondary">
                  {getPrimaryDescription(resolvedParam)}
                </Typography>
                {resolvedParam.units && resolvedParam.units.length > 1 && (
                  <Tooltip
                    title={t('dataCatalog.table.tooltips.multipleUnits', {
                      units: resolvedParam.units.map(u => u.unit).join(', '),
                    })}
                  >
                    <Chip
                      label={t('dataCatalog.table.tooltips.unitsCount', { count: resolvedParam.units.length })}
                      size="small"
                      color="warning"
                      sx={{ height: 16, fontSize: '0.6rem', fontWeight: 600 }}
                    />
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Tooltip>
        </TableCell>

        <TableCell>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {resolvedParam.tags.map(tag => {
              const tagData = allTagsWithStats.find(t => t.name === tag);
              const isPrivate = tagData?.isPrivate || false;
              const CustomIconComponent = tagData?.icon ? getIconComponent(tagData.icon) : null;

              return (
                <Tooltip
                  key={tag}
                  title={tagData?.createdBy ? t('dataCatalog.table.tooltips.createdBy', { user: tagData.createdBy }) : ''}
                >
                  <Chip
                    icon={
                      CustomIconComponent ? (
                        <CustomIconComponent fontSize="small" />
                      ) : isPrivate ? (
                        <LockIcon fontSize="small" />
                      ) : (
                        <PublicIcon fontSize="small" />
                      )
                    }
                    label={tag}
                    size="small"
                    color={isPrivate ? 'secondary' : 'default'}
                    variant="outlined"
                  />
                </Tooltip>
              );
            })}
            {resolvedParam.sourceSystem && (
              <Chip
                icon={<StorageIcon />}
                label={resolvedParam.sourceSystem}
                size="small"
                color="info"
                variant="filled"
                sx={{ fontWeight: 500 }}
              />
            )}
          </Box>
        </TableCell>

        <TableCell>
          <FrequencyCell
            caseCoverage={resolvedParam.frequencyMetrics?.caseCoverage ?? resolvedParam.frequency}
            stationCount={resolvedParam.frequencyMetrics?.stationCount ?? resolvedParam.stationCount}
          />
        </TableCell>

        {nameView !== 'charite' && (
          <TableCell>
            <Chip
              label={getOMOPStatusLabel(resolvedParam.omopStatus, t)}
              size="small"
              color={getOMOPStatusColor(resolvedParam.omopStatus)}
            />
            {resolvedParam.omopCode && (
              <Typography variant="caption" display="block" color="textSecondary">
                {resolvedParam.omopCode}
              </Typography>
            )}
          </TableCell>
        )}

        <TableCell>
          <SensitivityBadge level={resolvedParam.sensitivity} />
        </TableCell>

        <TableCell align="center">
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
            <FavoriteButton
              isFavorite={resolvedParam.isFavorite}
              onClick={() => onFavoriteToggle(resolvedParam.id)}
            />
            <ListButton
              isInList={isParameterInAnyList(resolvedParam.id)}
              onClick={(e) => handleOpenListDialog(resolvedParam.id, e)}
              activeListName={activeWorkingList.length === 1 && parameterLists ? parameterLists.find(l => l.id === activeWorkingList[0])?.name : null}
            />
            <IconButton
              size="small"
              component={Link}
              to={`/parameter/${getParameterLookupId(resolvedParam)}`}
              onClick={(e) => e.stopPropagation()}
              title={t('dataCatalog.table.tooltips.viewAllDetails')}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={nameView !== 'charite' ? (activeWorkingList.length > 0 ? 8 : 7) : (activeWorkingList.length > 0 ? 7 : 6)}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <ExpandedRow
              param={resolvedParam}
              parameterLists={parameterLists}
              activeWorkingList={activeWorkingList}
              handleOpenListDialog={handleOpenListDialog}
              loading={expandedLoading}
              error={detailError}
            />
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

export default ParameterRow;
